import { FrontendConstants } from '../common/FrontendConstants';
import { Settings } from '../common/Settings';
import { AuthClient } from '../exports';
import { CommonConstants } from '@shared/common';
import type { ISessionDTO, ITenantUserDTO, IUserDTO } from '@shared/common';

/** Optional context for backend login/session policy. */
interface ILoginContextDTO {
  app?: string;
  pin?: string;
  smsConsentAccepted?: boolean;
}

/** Minimal tenant link returned for portal user-detail cross-navigation. */
export interface IUserTenantLink {
  tenantID: string;
  name: string;
}

/** Owner-account link (plus the full owner contact record) returned for the portal tenant-detail Business/Contact panel. */
export interface ITenantOwnerLink {
  userID: string;
  username: string;
  contact: ITenantUserDTO;
}

/**
 * Client for user service operations.
 *
 * This client provides access to user management functionality exposed by the UserController.
 */
export class UserClient {
  /** Singleton instance. */
  private static instance: UserClient;
  /** Prevent external instantiation. */
  private constructor() {}

  /** @returns The singleton UserClient instance. */
  public static GetInstance(): UserClient {
    return UserClient.instance ?? (UserClient.instance = new UserClient());
  }

  /**
   * Parses a response body that may legitimately be empty into a nullable value. A handler that returns `null` (e.g. a user with no tenant, a tenant with no resolvable owner) is serialized by
   * NestJS as a 200 with an empty body, so calling `res.json()` directly would throw "Unexpected end of JSON input". An empty body resolves to `null`; any non-empty body is parsed as JSON.
   *
   * @param res - The fetch response whose body may be empty.
   * @returns The parsed value, or null when the body is empty.
   */
  private static async parseNullableJson<T>(res: Response): Promise<T | null> {
    const body = (await res.text()).trim();
    return body.length === 0 ? null : (JSON.parse(body) as T);
  }

  // Service Health

  /**
   * GET /api/user/base/health - Service health check.
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @returns 'OK' if the service is healthy.
   */
  public async HealthCheck(signal: AbortSignal): Promise<string> {
    const res = await AuthClient.Fetch(Settings.SERVICE_USER_URI, 'base/health', { signal });
    if (!res.ok) throw new Error('User service health check failed');
    return await res.text();
  }

  // Cache Clear

  /**
   * GET /api/user/gated/clear - Clear user-related caches/resources.
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @returns 'CLEARED' confirmation string.
   */
  public async Clear(signal: AbortSignal): Promise<string> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_USER_URI, 'gated/clear', { signal }, auth);
    if (!res.ok) throw new Error('Failed to clear user cache');
    return await res.text();
  }

  // Users (UserController)

  /**
   * GET /api/user/gated/users - Get users based on filter criteria (privileged).
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param offset - Number of users to skip for pagination.
   * @param limit - Maximum number of users to return.
   * @param pattern - Optional search by username pattern.
   * @returns Array of matching users.
   * @throws Error when the request fails or responds with a non-OK status.
   */
  public async GetUsers(signal: AbortSignal, offset: number = CommonConstants.Pagination.Offset, limit: number = CommonConstants.Pagination.Limit, pattern?: string): Promise<IUserDTO[]> {
    const params = new URLSearchParams({ limit: limit.toString(), offset: offset.toString() });
    if (pattern) params.append('pattern', pattern);
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_USER_URI, `gated/users?${params}`, { signal }, auth);
    if (!res.ok) throw new Error('Failed to fetch users');
    return await res.json();
  }

  /**
   * GET /api/user/gated/user/:userID - Get a specific user by ID (privileged or self).
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param userID - The user ID to fetch.
   * @returns The user data.
   * @throws Error when the request fails or responds with a non-OK status.
   */
  public async GetUserByID(signal: AbortSignal, userID: string): Promise<IUserDTO> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_USER_URI, `gated/user/${userID}`, { signal }, auth);
    if (!res.ok) throw new Error('Failed to fetch user details');
    return await res.json();
  }

  /**
   * GET /api/user/gated/user/:userID/tenant - Resolve the tenant a user belongs to (privileged).
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param userID - The user ID to resolve.
   * @returns The linked tenant, or null when the user belongs to no tenant.
   */
  public async GetUserTenant(signal: AbortSignal, userID: string): Promise<IUserTenantLink | null> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_USER_URI, `gated/user/${userID}/tenant`, { signal }, auth);
    if (!res.ok) throw new Error('Failed to fetch user tenant');
    return UserClient.parseNullableJson<IUserTenantLink>(res);
  }

  /**
   * GET /api/user/gated/tenant/:tenantID/owner - Resolve the owner account of a tenant (privileged).
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param tenantID - The tenant ID to resolve.
   * @returns The owner account link, or null when no owner account resolves.
   */
  public async GetTenantOwner(signal: AbortSignal, tenantID: string): Promise<ITenantOwnerLink | null> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_USER_URI, `gated/tenant/${tenantID}/owner`, { signal }, auth);
    if (!res.ok) throw new Error('Failed to fetch tenant owner');
    return UserClient.parseNullableJson<ITenantOwnerLink>(res);
  }

  /**
   * POST /api/user/gated/login - Login or create a new user.
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param userData - The user creation data.
   * @param loginContext - Optional app/pin context for backend login policy.
   * @returns The logged-in or newly created user.
   * @throws Error when the request fails or responds with a non-OK status.
   */
  public async LoginUser(signal: AbortSignal, userData: Partial<IUserDTO>, loginContext?: ILoginContextDTO): Promise<IUserDTO> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const loginBody = { ...userData, ...(loginContext || {}) };
    const res = await AuthClient.Fetch(
      Settings.SERVICE_USER_URI,
      'gated/login',
      {
        method: FrontendConstants.MethodPost,
        headers: { [FrontendConstants.HeaderContentType]: FrontendConstants.ContentTypeJson },
        body: JSON.stringify(loginBody),
        signal
      },
      auth
    );
    if (!res.ok) throw new Error('Failed to login user');
    return await res.json();
  }

  /**
   * PUT /api/user/gated/user/:userID - Update a user (privileged or self).
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param userID - The user ID to update.
   * @param updateData - The user update data.
   * @returns The updated user.
   * @throws Error when the request fails or responds with a non-OK status.
   */
  public async UpdateUser(signal: AbortSignal, userID: string, updateData: Partial<IUserDTO>): Promise<IUserDTO> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(
      Settings.SERVICE_USER_URI,
      `gated/user/${userID}`,
      {
        method: FrontendConstants.MethodPut,
        headers: { [FrontendConstants.HeaderContentType]: FrontendConstants.ContentTypeJson },
        body: JSON.stringify(updateData),
        signal
      },
      auth
    );
    if (!res.ok) {
      // 403 = authorization denial (e.g. caller cannot edit a peer/superior). Surface it with a distinguishable code so callers can render an
      // "Unauthorized" message and log at Debug instead of Error — it is a valid runtime outcome, not a fault.
      const isForbidden = res.status === FrontendConstants.HTTPStatusForbidden;
      const err = new Error(isForbidden ? FrontendConstants.ErrorCodeUnauthorized : 'Failed to update user') as Error & { code?: string };
      if (isForbidden) err.code = FrontendConstants.ErrorCodeUnauthorized;
      throw err;
    }
    return await res.json();
  }

  // Session

  /**
   * POST /api/user/gated/logout - Destroy the user's session.
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @returns Confirmation string.
   * @throws Error when the request fails or responds with a non-OK status.
   */
  public async LogoutUser(signal: AbortSignal): Promise<string> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(
      Settings.SERVICE_USER_URI,
      'gated/logout',
      {
        method: FrontendConstants.MethodPost,
        signal
      },
      auth
    );
    if (!res.ok) throw new Error('Failed to logout user');
    return await res.text();
  }

  // Counts

  /**
   * GET /api/user/gated/users/count - Get total count of users matching filter (privileged).
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param pattern - Optional search by username pattern.
   * @returns The count of matching users.
   */
  public async GetUsersCount(signal: AbortSignal, pattern?: string): Promise<number> {
    const params = new URLSearchParams();
    if (pattern) params.append('pattern', pattern);
    const auth = await AuthClient.GetAuthClientAdapter();
    const query = params.toString();
    const res = await AuthClient.Fetch(Settings.SERVICE_USER_URI, `gated/users/count${query ? `?${query}` : ''}`, { signal }, auth);
    if (!res.ok) throw new Error('Failed to fetch users count');
    return await res.json();
  }

  /**
   * GET /api/user/gated/users/csv - Download CSV of subscribed users (privileged).
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @returns The CSV file content as a Blob.
   * @throws Error when the request fails or responds with a non-OK status.
   */
  public async GetUsersCSV(signal: AbortSignal): Promise<Blob> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_USER_URI, 'gated/users/csv', { signal }, auth);
    if (!res.ok) throw new Error('Failed to fetch users CSV');
    return await res.blob();
  }

  /**
   * DELETE /api/user/gated/user/:userID - Delete a user (admin only).
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param userID - The user ID to delete.
   * @throws Error when the request fails or responds with a non-OK status.
   */
  public async DeleteUser(signal: AbortSignal, userID: string): Promise<void> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_USER_URI, `gated/user/${userID}`, { method: FrontendConstants.MethodDelete, signal }, auth);
    if (!res.ok) {
      const isForbidden = res.status === FrontendConstants.HTTPStatusForbidden;
      const err = new Error(isForbidden ? FrontendConstants.ErrorCodeUnauthorized : 'Failed to delete user') as Error & { code?: string };
      if (isForbidden) err.code = FrontendConstants.ErrorCodeUnauthorized;
      throw err;
    }
  }

  // Sessions (UserController)

  /**
   * GET /api/user/gated/sessions - List sessions, newest first (privileged).
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param offset - Number of sessions to skip for pagination.
   * @param limit - Maximum number of sessions to return.
   * @returns Array of sessions.
   * @throws Error when the request fails or responds with a non-OK status.
   */
  public async GetSessions(signal: AbortSignal, offset: number = CommonConstants.Pagination.Offset, limit: number = CommonConstants.Pagination.Limit): Promise<ISessionDTO[]> {
    const params = new URLSearchParams({ limit: limit.toString(), offset: offset.toString() });
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_USER_URI, `gated/sessions?${params}`, { signal }, auth);
    if (!res.ok) throw new Error('Failed to fetch sessions');
    return await res.json();
  }

  /**
   * GET /api/user/gated/sessions/count - Get total count of sessions (privileged).
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @returns The count of sessions.
   * @throws Error when the request fails or responds with a non-OK status.
   */
  public async GetSessionsCount(signal: AbortSignal): Promise<number> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_USER_URI, 'gated/sessions/count', { signal }, auth);
    if (!res.ok) throw new Error('Failed to fetch sessions count');
    return await res.json();
  }

  /**
   * GET /api/user/gated/session/:sessionID - Get a specific session by ID (privileged).
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param sessionID - The session ID to fetch.
   * @returns The session data.
   * @throws Error when the request fails or responds with a non-OK status.
   */
  public async GetSessionByID(signal: AbortSignal, sessionID: string): Promise<ISessionDTO> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_USER_URI, `gated/session/${sessionID}`, { signal }, auth);
    if (!res.ok) throw new Error('Failed to fetch session details');
    return await res.json();
  }

  // Signup capture (public — onboarding before account exists)

  /**
   * POST /api/user/base/signup - Capture (or refresh) a signup session keyed by email. Public/unauthenticated.
   *
   * When the email already belongs to a registered account the server silently no-ops (no state merge, no IP/User-Agent re-stamp) and returns the untouched session - or an empty body when no
   * signup session exists - so the endpoint cannot poison a registered user's staged onboarding or act as an enumeration oracle.
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param email - The prospect's email (lookup key).
   * @param state - The staged onboarding form fields to merge into the session state.
   * @returns The created or refreshed signup session; null when the email is registered and has no signup session.
   * @throws Error when the request fails or responds with a non-OK status.
   */
  public async CaptureSignup(signal: AbortSignal, email: string, state?: Record<string, string>): Promise<ISessionDTO | null> {
    const res = await AuthClient.Fetch(Settings.SERVICE_USER_URI, 'base/signup', {
      method: FrontendConstants.MethodPost,
      headers: { [FrontendConstants.HeaderContentType]: FrontendConstants.ContentTypeJson },
      body: JSON.stringify({ email, state }),
      signal
    });
    if (!res.ok) throw new Error('Failed to capture signup');
    // The registered-email no-op path returns a 200 with an empty body, which res.json() would reject on.
    return UserClient.parseNullableJson(res);
  }

  /**
   * GET /api/user/base/signup - Retrieve the staged onboarding form for the caller's own email to resume onboarding.
   *
   * Authenticated: the service serves the staged fields (`state`) only when the verified Firebase token email matches the requested email (case-insensitive), so this attaches the Bearer token
   * like every other identity-bound call. The server still withholds the session's IP/User-Agent/identity.
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param email - The prospect's email (must match the caller's token email).
   * @returns The staged onboarding form fields, or null when none exists.
   * @throws Error when the request fails with a non-OK status.
   */
  public async GetSignup(signal: AbortSignal, email: string): Promise<Pick<ISessionDTO, 'state'> | null> {
    const params = new URLSearchParams({ email });
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_USER_URI, `base/signup?${params}`, { signal }, auth);
    if (!res.ok) throw new Error('Failed to fetch signup');
    // The endpoint returns a 200 with `state: null` when no signup session exists (an expected, non-error outcome), so map that to null for callers.
    const body = (await res.json()) as Pick<ISessionDTO, 'state'> | null;
    return body?.state == null ? null : body;
  }
}
